<!--
  Fixed order, fixed positions. See the component class: a row is never
  omitted, only drawn empty, because alignment ACROSS cards is the whole
  point of this grid.

  `title` on each row carries the same wording the legend uses, so the
  meaning of a colour is reachable without scrolling back up to the key.
-->
<div class="facetGrid" [class.compact]="compact">
  <div class="facetRow"
       *ngFor="let row of rows"
       [ngClass]="['facet-' + row.strength, row.comparison ? 'cmp-' + row.comparison : '']"
       [attr.data-facet]="row.key">

    <span class="facetLabel" *ngIf="!compact" [ngSwitch]="row.key">
      <span *ngSwitchCase="'TITLE'"      i18n="@@Title">Title</span>
      <span *ngSwitchCase="'AUTHOR'"     i18n="@@Author">Author</span>
      <span *ngSwitchCase="'BODY'"       i18n="@@FacetInteriorPdf">Interior PDF</span>
      <span *ngSwitchCase="'COVER'"      i18n="@@FacetCoverPdf">Cover PDF</span>
      <span *ngSwitchCase="'PAGES'"      i18n="@@Pages">Pages</span>
      <span *ngSwitchCase="'TRIM'"       i18n="@@Trim Size">Trim Size</span>
      <span *ngSwitchCase="'BINDING'"    i18n="@@Binding Type">Binding Type</span>
      <span *ngSwitchCase="'PAPER'"      i18n="@@Paper Type">Paper Type</span>
      <span *ngSwitchCase="'LAMINATION'" i18n="@@Lamination Type">Lamination Type</span>
      <span *ngSwitchCase="'IDENTIFIER'" i18n="@@FacetIdentifier">ISBN</span>
      <span *ngSwitchCase="'PROJECT'"    i18n="@@FacetExistingProject">Existing Project</span>
      <span *ngSwitchCase="'UPDATED'"    i18n="@@FacetLastEdited">Last edited</span>
      <span *ngSwitchDefault>{{ row.key }}</span>
    </span>

    <!--
      Angular's date pipe, not a string formatted in TypeScript: the pipe
      formats to the build's LOCALE_ID, whereas toLocaleDateString would use
      the BROWSER's locale and quietly disagree with every other date in the
      app on any machine whose two settings differ.
    -->
    <span class="facetValue" *ngIf="row.valueDate">{{ row.valueDate | date:'mediumDate' }}</span>
    <!--
      The full value on hover.

      Values are ellipsised to one line on purpose - a wrapped value on one
      card pushes its neighbours out of step and destroys the cross-card
      alignment this grid exists for. But a truncated value under a "differs"
      mark is unverifiable: the reader is told two titles disagree and shown
      two identical-looking prefixes, with no way to see where. The tooltip
      costs no layout and makes the claim checkable.
    -->
    <span class="facetValue" *ngIf="!row.valueDate && row.value" [title]="row.value">{{ row.value }}</span>
    <!--
      A dash, not an empty span. An item with no ISBN and an item whose ISBN
      has not loaded look the same here, and both are better read as "nothing
      recorded" than as a row that failed to render.
    -->
    <span class="facetValue facetValueEmpty" *ngIf="!row.value && !row.valueDate" aria-hidden="true">&mdash;</span>

    <!--
      Said in words as well as colour. Colour alone fails for the ~8% of men
      with a colour vision deficiency, and this grid is the entire argument
      for a grouping. Only the strengths that CLAIM something are marked;
      tagging every neutral row would bury the three that matter.
    -->
    <!--
      How far this item is from the newest in its group.

      The absolute date alone does not answer "is this the current one?" -
      that is a relative question, and subtracting dates across six cards by
      eye is exactly the work this grid exists to remove. Shown instead of the
      strength tag on this row, because UPDATED is never evidence of a match
      and would otherwise always read "not part of this match".
    -->
    <span class="facetTag facetRecency" *ngIf="row.detailKind" [ngSwitch]="row.detailKind">
      <span *ngSwitchCase="'MOST_RECENT'" class="recencyNewest" i18n="@@FacetMostRecent">most recent</span>
      <span *ngSwitchCase="'DAYS_OLDER'"><span class="recencyDays">{{ row.detail }}</span>&nbsp;<span i18n="@@FacetDaysOlder">days older</span></span>
      <span *ngSwitchCase="'DAYS_NEWER'"><span class="recencyDays">{{ row.detail }}</span>&nbsp;<span i18n="@@FacetDaysNewer">days newer</span></span>
    </span>

    <!--
      Id order and edit order disagree for this item.
      `RECENCY` and `ID` would pick different primaries, so the proposed one
      is only as good as whichever rule was applied - exactly the case to look
      at rather than accept.
    -->
    <mat-icon class="sequenceFlag" *ngIf="row.key === 'UPDATED' && sequenceAnomaly"
              title="Edited out of order compared with when it was created"
              i18n-title="@@FacetSequenceAnomalyToolTip">swap_vert</mat-icon>

    <!--
      Said in words as well as colour, for the same reason the strength tag is.
      Only DIFFERENCES are labelled: tagging every agreeing row would put a
      word on almost every line and bury the handful that matter.
    -->
    <!--
      Resource ids are issued in ascending order, so a difference between two
      of them has a direction. "Newer" answers the reader's actual question -
      which artwork came second - where "differs" leaves them comparing digits.
      Only for the id rows; pages and trim differ without either being newer.
    -->
    <span class="facetTag cmpTag" *ngIf="!row.detailKind && row.direction"
          [ngSwitch]="row.direction">
      <span *ngSwitchCase="'NEWER'" i18n="@@FacetValueNewer">newer</span>
      <span *ngSwitchCase="'OLDER'" i18n="@@FacetValueOlder">older</span>
    </span>

    <span class="facetTag cmpTag" *ngIf="!row.detailKind && !row.direction && row.comparison && row.comparison !== 'SAME'"
          [ngSwitch]="row.comparison">
      <span *ngSwitchCase="'DIFF_MAJOR'" i18n="@@FacetDiffersFromPreviousMajor">differs</span>
      <span *ngSwitchCase="'DIFF_MINOR'" i18n="@@FacetDiffersFromPrevious">differs</span>
    </span>

    <span class="facetTag" *ngIf="!row.detailKind && (!row.comparison || row.comparison === 'SAME')" [ngSwitch]="row.strength">
      <span *ngSwitchCase="'ANCHOR'"        i18n="@@FacetStrengthAnchor">starting point</span>
      <span *ngSwitchCase="'DECISIVE'"      i18n="@@FacetStrengthDecisive">match</span>
      <span *ngSwitchCase="'SUPPORTING'"    i18n="@@FacetStrengthSupporting">also matches</span>
      <span *ngSwitchCase="'CORROBORATING'" i18n="@@FacetStrengthCorroborating">agrees only</span>
      <span *ngSwitchCase="'WEAK'"          i18n="@@FacetStrengthWeak">close</span>
      <span *ngSwitchCase="'DIFFERS'"       i18n="@@FacetStrengthDiffers">differs</span>
      <span *ngSwitchDefault></span>
    </span>
  </div>
</div>
